import React, { useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useNavigate, useParams } from "react-router-dom";
import { useDocumentTitle } from "$utils/document-title.js";
import Breadcrumb from "$components/breadcrumb";
import NotificationBar from "$components/notification-bar";
import ShadowTable from "$components/loaders/shadow-table";
import { formatDate, SLASH_DATE_FORMAT } from "$utils/date-helpers.js";
import api from "$services/api";
import { initiativesApi } from "../api";
import {
    InitiativeDetailsStore,
    resetInitiativeDetails,
    setInitiativeError,
    setInitiativeLoaded,
    setFollowUps,
    setShortlistCount,
    updateInitiativeInStore,
} from "$stores/initiative-details";
import { initiateModal } from "$stores/modal";
import { AppContextStore } from "$stores/app-context";
import { UserStore } from "$stores/user";
import ProfileIcon from "$components/profile-icon";
import CollaboratorModal from "./CollaboratorModal";
import InitiativeNav from "./initiative-nav";
import { appInsights } from "$utils/app-insights";
import { getTerm } from "$stores/dictionary";
import { getFocusTypeLabels } from "../libs/columns";
import "./initiative-detail.scss";

const STATUSES = ["Active", "Complete", "Archived"];

const STATUS_COLORS = {
    Active: "badge-success",
    Complete: "badge-primary",
    Archived: "badge-secondary",
};

// nameLookup: { [id]: displayName } — used for old-format data that only has ids, not items
const parseFocusTargetItems = (focusTargetJson, nameLookup = {}) => {
    if (!focusTargetJson) return [];
    try {
        const parsed = JSON.parse(focusTargetJson);
        if (Array.isArray(parsed?.items) && parsed.items.length > 0) return parsed.items;
        if (Array.isArray(parsed?.ids)) {
            return parsed.ids
                .map((v) => {
                    const text = nameLookup[v];
                    if (!text) appInsights.trackEvent({
                        name: "FocusTargetUnresolved",
                        properties: { initiativeId: initiative?.id, focusType: initiative?.focusType, unresolvableId: v },
                    });
                    return { value: v, text };
                })
                .filter((item) => item.text);
        }
        return [];
    } catch {
        return [];
    }
};

const InitiativeDetailLayout = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { initiative, dismissOptions, collaborators, followUps, shortlistCount, isLoading, error } =
        InitiativeDetailsStore.useState((s) => s);
    const filterMenu = AppContextStore.useState((s) => s.filterMenu);
    const currentUserGuid = UserStore.useState((s) => s.account?.localAccountId);
    const isOwner = !!currentUserGuid && initiative?.ownerId?.toLowerCase() === currentUserGuid.toLowerCase();
    const [focusIdLookup, setFocusIdLookup] = useState({});
    const focusNameLookup = React.useMemo(() => {
        const asyncTypes = new Set(["PartnerClients", "TopClients", "AtRiskClients"]);
        if (asyncTypes.has(initiative?.focusType)) return focusIdLookup;
        const filterKey = { PracticeArea: "practice", Industry: "industry" }[initiative?.focusType];
        if (!filterKey) return {};
        const options = filterMenu.find((f) => f.key === filterKey)?.values || [];
        return Object.fromEntries(options.map((o) => [o.value, o.text]));
    }, [initiative?.focusType, filterMenu, focusIdLookup]);

    useDocumentTitle(initiative ? initiative.name : getTerm("initiative"));

    useEffect(() => {
        resetInitiativeDetails();

        const controller = new AbortController();
        const { signal } = controller;

        setFocusIdLookup({});

        Promise.all([
            initiativesApi.get(id, signal),
            initiativesApi.getDismissOptions(signal),
            initiativesApi.getCollaborators(id, signal),
            initiativesApi.getFollowUps(id, signal),
            initiativesApi.getShortlist(id, signal),
        ])
            .then(([init, opts, collab, fups, shortlist]) => {
                if (!init) {
                    setInitiativeError(`${getTerm("initiative")} not found or you don't have access.`);
                    return;
                }
                setInitiativeLoaded({
                    initiative: init,
                    dismissOptions: opts || [],
                    collaborators: collab || [],
                });
                setFollowUps(fups || []);
                setShortlistCount((shortlist || []).length);

                // Resolve IDs to names for old-format focus targets (ids only, no items array)
                const asyncFocusTypes = new Set(["PartnerClients", "TopClients", "AtRiskClients"]);
                if (asyncFocusTypes.has(init.focusType) && init.focusTargetJson) {
                        try {
                        const parsed = JSON.parse(init.focusTargetJson);
                        const needsLookup =
                            Array.isArray(parsed?.ids) &&
                            parsed.ids.length > 0 &&
                            (!Array.isArray(parsed?.items) || parsed.items.length === 0);
                        if (needsLookup) {
                            if (init.focusType === "PartnerClients") {
                                Promise.all(
                                    parsed.ids.map((empId) =>
                                        api.get(`/api/employees/${empId}`, null, { signal })
                                    )
                                )
                                    .then((results) => {
                                        const lookup = {};
                                        results.forEach((r) => {
                                            const emp = r?.employees?.[0];
                                            if (emp) lookup[emp.employeeId] = emp.fullName;
                                        });
                                        setFocusIdLookup(lookup);
                                    })
                                    .catch(() => {});
                            } else {
                                // TopClients / AtRiskClients: batch client lookup
                                const qs = parsed.ids.map((id) => `ids=${encodeURIComponent(id)}`).join("&");
                                api.get(`/api/clients/by-ids?${qs}`, null, { signal })
                                    .then((data) => {
                                        const lookup = {};
                                        (data?.data || []).forEach((c) => { lookup[c.id] = c.name; });
                                        setFocusIdLookup(lookup);
                                    })
                                    .catch(() => {});
                            }
                        }
                    } catch {}
                }
            })
            .catch((err) => {
                if (err?.message !== "canceled") {
                    setInitiativeError(`${getTerm("initiative")} not found or you don't have access.`);
                }
            });

        return () => {
            controller.abort();
            resetInitiativeDetails();
        };
    }, [id]);

    const changeStatus = async (newStatus) => {
        if (newStatus === initiative.status) return;
        const prev = initiative.status;
        updateInitiativeInStore({ status: newStatus });
        try {
            await initiativesApi.updateStatus(id, newStatus);
        } catch {
            updateInitiativeInStore({ status: prev });
        }
    };

    const savingNameRef = useRef(false);

    // Inline name editing
    const [editingName, setEditingNameState] = React.useState(false);
    const [nameValue, setNameValue] = React.useState("");
    const [nameSaving, setNameSaving] = React.useState(false);

    React.useEffect(() => {
        if (initiative?.name && !editingName) {
            setNameValue(initiative.name);
        }
    }, [initiative?.name, editingName]);

    const saveName = async () => {
        if (savingNameRef.current) return;
        if (!nameValue.trim() || nameValue.trim() === initiative.name) {
            setEditingNameState(false);
            setNameValue(initiative.name);
            return;
        }
        savingNameRef.current = true;
        setNameSaving(true);
        const prev = initiative.name;
        const next = nameValue.trim();
        updateInitiativeInStore({ name: next });
        setEditingNameState(false);
        try {
            await initiativesApi.update(id, {
                name: next,
                status: initiative.status,
                summaryText: initiative.summaryText || null,
                targetDate: initiative.targetDate || null,
            });
        } catch {
            updateInitiativeInStore({ name: prev });
            setNameValue(prev);
            setEditingNameState(true);
        } finally {
            setNameSaving(false);
            savingNameRef.current = false;
        }
    };

    // Inline summary editing
    const savingSummaryRef = React.useRef(false);
    const [editingSummary, setEditingSummary] = React.useState(false);
    const [summaryValue, setSummaryValue] = React.useState("");

    React.useEffect(() => {
        if (initiative?.summaryText !== undefined && !editingSummary) {
            setSummaryValue(initiative.summaryText || "");
        }
    }, [initiative?.summaryText, editingSummary]);

    const saveSummary = async () => {
        if (savingSummaryRef.current) return;
        const trimmed = summaryValue.trim();
        const prev = initiative.summaryText || "";
        if (trimmed === prev) {
            setEditingSummary(false);
            return;
        }
        savingSummaryRef.current = true;
        updateInitiativeInStore({ summaryText: trimmed || null });
        setEditingSummary(false);
        try {
            await initiativesApi.update(id, {
                name: initiative.name,
                status: initiative.status,
                summaryText: trimmed || null,
                targetDate: initiative.targetDate || null,
            });
        } catch {
            updateInitiativeInStore({ summaryText: prev || null });
            setSummaryValue(prev);
            setEditingSummary(true);
        } finally {
            savingSummaryRef.current = false;
        }
    };

    // Inline target date editing
    const [editingTargetDate, setEditingTargetDate] = React.useState(false);
    const [targetDateValue, setTargetDateValue] = React.useState("");

    React.useEffect(() => {
        if (!editingTargetDate) {
            const d = initiative?.targetDate ? new Date(initiative.targetDate) : null;
            setTargetDateValue(d ? d.toISOString().slice(0, 10) : "");
        }
    }, [initiative?.targetDate, editingTargetDate]);

    const saveTargetDate = async () => {
        const prev = initiative.targetDate;
        const newDate = targetDateValue ? new Date(targetDateValue + "T00:00:00Z") : null;
        setEditingTargetDate(false);
        updateInitiativeInStore({ targetDate: newDate?.toISOString() || null });
        try {
            await initiativesApi.update(id, {
                name: initiative.name,
                status: initiative.status,
                summaryText: initiative.summaryText || null,
                targetDate: newDate?.toISOString() || null,
            });
        } catch {
            updateInitiativeInStore({ targetDate: prev });
            setTargetDateValue(prev ? new Date(prev).toISOString().slice(0, 10) : "");
        }
    };

    // Derived: next step from follow-ups
    const nextStep = React.useMemo(() => {
        if (!followUps || followUps.length === 0) return null;
        const open = followUps.filter((f) => f.status === "open" && f.dueDate);
        if (open.length === 0) return null;
        return open.reduce((a, b) => new Date(a.dueDate) <= new Date(b.dueDate) ? a : b);
    }, [followUps]);

    const breadcrumbLinks = [
        { text: getTerm("initiatives"), url: "/initiatives" },
        { text: initiative?.name || getTerm("initiative"), url: `/initiatives/${id}` },
    ];

    if (isLoading) {
        return (
            <div className="page-container">
                <div id="initiative" className="page">
                    <div className="main">
                        <ShadowTable />
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page-container">
                <div id="initiative" className="page">
                    <div className="main">
                        <NotificationBar type="error">{error}</NotificationBar>
                    </div>
                </div>
            </div>
        );
    }

    const isTerminal = initiative.status === "Complete" || initiative.status === "Archived";
    const isTargetOverdue = initiative.targetDate
        && new Date(initiative.targetDate) < new Date()
        && !isTerminal;
    const isNextStepOverdue = nextStep && new Date(nextStep.dueDate) < new Date();
    const focusItems = parseFocusTargetItems(initiative.focusTargetJson, focusNameLookup);

    return (
        <div className="page-container">
            <div id="initiative" className="page">
                <Breadcrumb links={breadcrumbLinks} />
                <div className="main">
                    <main>
                        <div className="main-wrapper">
                            <div className="page-content -shadow">
                                <div className="panel-content">
                                    {/* Overview bar — horizontal strip */}
                                    <div className="initiative-overview">
                                        <div className="initiative-overview__title">
                                            <span className="initiative-overview__label">Name</span>
                                            <div className="initiative-overview__title-row">
                                                {editingName ? (
                                                    <input
                                                        className="initiative-overview__name-input"
                                                        value={nameValue}
                                                        autoFocus
                                                        disabled={nameSaving}
                                                        onChange={(e) => setNameValue(e.target.value)}
                                                        onBlur={saveName}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") saveName();
                                                            if (e.key === "Escape") {
                                                                setEditingNameState(false);
                                                                setNameValue(initiative.name);
                                                            }
                                                        }}
                                                    />
                                                ) : (
                                                    <h2
                                                        className="initiative-overview__name"
                                                        onClick={() => setEditingNameState(true)}
                                                        title="Click to rename"
                                                    >
                                                        {initiative.name}
                                                    </h2>
                                                )}
                                                <select
                                                    className={`badge ${STATUS_COLORS[initiative.status] || "badge-secondary"} initiative-overview__stage-select`}
                                                    value={initiative.status}
                                                    onChange={(e) => changeStatus(e.target.value)}
                                                >
                                                    {STATUSES.map((s) => (
                                                        <option key={s} value={s}>{s}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            {/* Summary inline under name */}
                                            {editingSummary ? (
                                                <textarea
                                                    className="initiative-overview__summary-input"
                                                    value={summaryValue}
                                                    autoFocus
                                                    rows={2}
                                                    onChange={(e) => setSummaryValue(e.target.value)}
                                                    onBlur={saveSummary}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") { e.preventDefault(); saveSummary(); }
                                                        if (e.key === "Escape") {
                                                            setEditingSummary(false);
                                                            setSummaryValue(initiative.summaryText || "");
                                                        }
                                                    }}
                                                />
                                            ) : initiative.summaryText ? (
                                                <p
                                                    className="initiative-overview__summary"
                                                    onClick={() => setEditingSummary(true)}
                                                    title="Click to edit summary"
                                                >
                                                    {initiative.summaryText}
                                                </p>
                                            ) : (
                                                <span
                                                    className="initiative-overview__summary-placeholder"
                                                    onClick={() => { setEditingSummary(true); setSummaryValue(""); }}
                                                >
                                                    + Add summary
                                                </span>
                                            )}
                                        </div>
                                        <div className="divide" />
                                        <div className="initiative-overview__meta">
                                            <span className="initiative-overview__label">Focus</span>
                                            <span className="initiative-overview__value">
                                                {getFocusTypeLabels()[initiative.focusType] || initiative.focusType}
                                            </span>
                                            {focusItems.length > 0 && (
                                                <div className="initiative-overview__focus-chips">
                                                    {focusItems.map((item, i) => (
                                                        <span key={i} className="initiative-overview__focus-chip">{item.text}</span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="divide" />
                                        <div className="initiative-overview__meta">
                                            <span className="initiative-overview__label">Team</span>
                                            <div className="initiative-overview__team">
                                                <div className="initiative-overview__team-owner">
                                                    <ProfileIcon name={initiative.ownerName} modifierClassNames={["-medium"]} />
                                                    <span className="initiative-overview__person-name">{initiative.ownerName}</span>
                                                    <span className="initiative-overview__person-role">Owner</span>
                                                </div>
                                                {collaborators.filter((c) => c.userId !== initiative.ownerId).length > 0 && (
                                                    <div className="initiative-overview__team-collabs">
                                                        {collaborators
                                                            .filter((c) => c.userId !== initiative.ownerId)
                                                            .map((c) => (
                                                                <div key={c.userId} className="initiative-overview__person">
                                                                    <ProfileIcon name={c.userName} modifierClassNames={["-small"]} />
                                                                    <span className="initiative-overview__person-name">{c.userName}</span>
                                                                </div>
                                                            ))}
                                                    </div>
                                                )}
                                                {isOwner && (
                                                    <button
                                                        className="button -secondary -small"
                                                        onClick={() =>
                                                            initiateModal(
                                                                CollaboratorModal,
                                                                { initiativeId: initiative.id, isOpen: true },
                                                                { analyticsLabel: "Modal - Manage Team" }
                                                            )
                                                        }
                                                    >
                                                        Invite
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="divide" />
                                        <div className="initiative-overview__meta">
                                            <span className="initiative-overview__label">Shortlist</span>
                                            <span className="initiative-overview__value">
                                                {shortlistCount} {shortlistCount === 1 ? "opportunity" : "opportunities"}
                                            </span>
                                        </div>
                                        <div className="divide" />
                                        {/* Target Date */}
                                        <div className="initiative-overview__meta">
                                            <span className="initiative-overview__label">Target</span>
                                            {editingTargetDate ? (
                                                <input
                                                    type="date"
                                                    className="initiative-overview__date-input"
                                                    value={targetDateValue}
                                                    autoFocus
                                                    onChange={(e) => setTargetDateValue(e.target.value)}
                                                    onBlur={saveTargetDate}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") saveTargetDate();
                                                        if (e.key === "Escape") {
                                                            setEditingTargetDate(false);
                                                            const d = initiative?.targetDate ? new Date(initiative.targetDate) : null;
                                                            setTargetDateValue(d ? d.toISOString().slice(0, 10) : "");
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <span
                                                    className={`initiative-overview__value${isTargetOverdue ? " initiative-overview__value--overdue" : ""}`}
                                                    onClick={() => setEditingTargetDate(true)}
                                                    title="Click to set target date"
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    {initiative.targetDate
                                                        ? formatDate(initiative.targetDate, SLASH_DATE_FORMAT)
                                                        : <span className="initiative-overview__value--muted">Set date</span>}
                                                </span>
                                            )}
                                        </div>
                                        {/* Next Step */}
                                        {nextStep && (
                                            <>
                                                <div className="divide" />
                                                <div className="initiative-overview__meta">
                                                    <span className="initiative-overview__label">Next Step</span>
                                                    <span
                                                        className={`initiative-overview__value${isNextStepOverdue ? " initiative-overview__value--overdue" : ""}`}
                                                        title={nextStep.commitmentText}
                                                    >
                                                        {(nextStep.commitmentText?.length ?? 0) > 40
                                                            ? nextStep.commitmentText.slice(0, 40) + "…"
                                                            : (nextStep.commitmentText || "—")}
                                                        {" "}
                                                        <span className="initiative-overview__value--muted">
                                                            {formatDate(nextStep.dueDate, SLASH_DATE_FORMAT)}
                                                        </span>
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    <InitiativeNav initiativeId={id} />

                                    <div className="initiative-body">
                                        <Outlet />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
};

export default InitiativeDetailLayout;
